/**
 * Build the `X-Device-Assertion` header value for machine-bound restricted-device enforcement.
 *
 * Flow: fetch a short-lived challenge nonce from the backend → ask the local Windows Agent to sign
 * it → return the signed assertion, base64url-encoded, for the request header. Returns null when the
 * agent is not present (unmanaged machine), in which case the request proceeds without machine proof
 * and the backend applies its fail-closed / open-default policy per device.
 */
import { envConfig } from '../config/environment';
import {
  getWindowsAgentHealth,
  getDeviceAssertionFromAgent,
  type AgentLocalHealth,
} from './agentLocalAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || envConfig.apiUrl;

function base64UrlEncode(text: string): string {
  const b64 = btoa(unescape(encodeURIComponent(text)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function fetchChallengeNonce(): Promise<string | null> {
  try {
    // Raw fetch (not the axios client) so this does not recurse through the request interceptor.
    const response = await fetch(`${API_BASE_URL}/devices/assertion-challenge`, { method: 'GET' });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const nonce = data?.data?.nonce;
    return typeof nonce === 'string' && nonce ? nonce : null;
  } catch {
    return null;
  }
}

/** Returns the header value, or null if no machine proof is available on this device. */
export async function getDeviceAssertionHeader(): Promise<string | null> {
  try {
    if (!(await getWindowsAgentHealth())) {
      return null;
    }
    const nonce = await fetchChallengeNonce();
    if (!nonce) {
      return null;
    }
    const assertion = await getDeviceAssertionFromAgent(nonce);
    if (!assertion || typeof assertion !== 'object') {
      return null;
    }
    return base64UrlEncode(JSON.stringify(assertion));
  } catch {
    return null;
  }
}

export type AgentMachineInfo = {
  deviceId: string;
  version?: string;
  assertionVerified: boolean;
};

/**
 * Returns the physical machine's device ID as reported by the local Windows Agent, or null when no
 * agent is present. Prefers /health.deviceId so Device Restrictions can show "Agent detected" even
 * if challenge signing is temporarily unavailable; upgrades to the signed assertion deviceId when
 * the assertion path works.
 */
export async function getAgentMachineInfo(): Promise<AgentMachineInfo | null> {
  try {
    const health: AgentLocalHealth | null = await getWindowsAgentHealth();
    if (!health) {
      return null;
    }

    const healthDeviceId =
      typeof health.deviceId === 'string' && health.deviceId.trim() ? health.deviceId.trim() : null;

    let assertionDeviceId: string | null = null;
    try {
      const nonce = await fetchChallengeNonce();
      if (nonce) {
        const assertion = await getDeviceAssertionFromAgent(nonce);
        if (assertion && typeof assertion === 'object') {
          const id = (assertion as { deviceId?: unknown }).deviceId;
          if (typeof id === 'string' && id.trim()) {
            assertionDeviceId = id.trim();
          }
        }
      }
    } catch {
      // Assertion is optional for display; health.deviceId is enough to show Agent present.
    }

    const deviceId = assertionDeviceId || healthDeviceId;
    if (!deviceId) {
      return null;
    }

    return {
      deviceId,
      version: typeof health.version === 'string' ? health.version : undefined,
      assertionVerified: Boolean(assertionDeviceId),
    };
  } catch {
    return null;
  }
}

/** @deprecated Prefer getAgentMachineInfo(); kept for call sites that only need the device ID. */
export async function getAgentMachineDeviceId(): Promise<string | null> {
  const info = await getAgentMachineInfo();
  return info?.deviceId ?? null;
}

/** Request URLs that must carry a device assertion when one is available. */
const ASSERTION_URL_PATTERNS = [
  '/auth/login',
  '/time-entries/clock-in',
  '/time-entries/clock-out',
  '/time-entries/break-start',
  '/time-entries/break-end',
  '/time-entries/bathroom-start',
];

export function requestNeedsDeviceAssertion(url: string | undefined): boolean {
  if (!url) return false;
  return ASSERTION_URL_PATTERNS.some((p) => url.includes(p));
}
